import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { useDispatch } from "react-redux";
import { ResourceError } from "../../../components/utils/ResourceError";
import { PMTabs } from "../../../components/utils/PMTabs";
import { CustomerInfo } from "../../../components/dashboard/customers/customer-info";
import PMLinkCard from "../../../components/utils/PMLinkCard";
import {
  BuildingOffice2Icon,
  Cog6ToothIcon,
  UsersIcon,
  PaperClipIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import { destroyCustomer, fetchCustomer } from "../../../api/customer";
import { axiosWithToast, mapErrors } from "../../../components/Utils";
import CaseDialog from "../../../components/dashboard/cases/case-dialog";
import { CasesStackList } from "../../../components/dashboard/cases/cases-stack-list";
import ServiceReportEditModal from "../../../components/dashboard/service/reports/service-report-edit-modal";
import { ReportsStackList } from "../../../components/dashboard/service/reports/reports-stack-list";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ServiceAgreementsStackList } from "../../../components/dashboard/service/agreements/agreements-stack-list";
import { ProjectsStackList } from "../../../components/dashboard/projects/projects-stack-list";
import ServiceAgreementModal from "../../../components/dashboard/service/agreements/modal";
import ProjectDialog from "../../../components/dashboard/projects/dialog";
import { OrdersStacklist } from "../../../components/dashboard/orders/orders-stack-list";
import SimplePrompt from "../../../components/utils/simplePrompt";
import { HistoriesModalButton } from "../../../components/dashboard/histories/modal-button";
import { useUserHasAccess } from "../../../routes/roleOnly";

export const Customer = (props) => {
  const rightSideActions = [
    {
      label: "New Case",
      description: "Create a new case",
      path: "",
      action: () => setCaseDialogOpen(true),
      icon: PaperClipIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: true,
    },
    {
      label: "Service Report",
      description: "Create a service report",
      path: "",
      action: () => setReportDialogOpen(true),
      icon: Cog6ToothIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: true,
    },
    {
      label: "Service Agreement",
      description: "Create a service agreement",
      path: "",
      action: () => setServiceAgreementDialogOpen(true),
      icon: UsersIcon,
      iconForeground: "text-purple-700",
      iconBackground: "bg-purple-50",
      active: true,
    },
    {
      label: "Project",
      description: "Create a project",
      path: "",
      action: () => setProjectDialogOpen(true),
      icon: BuildingOffice2Icon,
      iconForeground: "text-sky-700",
      iconBackground: "bg-sky-50",
      active: true,
    },
    // {
    //   label: "Order",
    //   description: "Create a order",
    //   path: "",
    //   action: () => toast.error("This function is not available"),
    //   icon: BanknotesIcon,
    //   iconForeground: "text-yellow-700",
    //   iconBackground: "bg-yellow-50",
    // },
  ];

  const [caseDialogOpen, setCaseDialogOpen] = useState(false);
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    customer: {},
  });

  // Deconstruct itemState (Store)
  const { isLoading, error, errors, customer } = itemState;
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);

  const item = customer;

  useEffect(() => {
    getCustomer();
  }, [dispatch, id]);

  const getCustomer = async () => {
    return fetchCustomer(id)
      .then((res) => {
        if (res.data.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            customer: res.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err.data.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          customer: {},
          error: true,
          errors: errors,
        }));
      });
  };

  const handleRefresh = (data) => {
    setItemState((prevState) => ({
      ...prevState,
      customer: data,
      isLoading: false,
    }));
  };

  const handleLoading = () => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
  };

  const [selectedTab, setSelectedTab] = useState(0);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [serviceAgreementDialogOpen, setServiceAgreementDialogOpen] =
    useState(false);

  const tabs = [
    {
      name: "Detaljer",
      component: (
        <CustomerInfo
          item={item}
          onRefresh={(data) => handleRefresh(data)}
          onLoading={() => handleLoading()}
        />
      ),
    },
    {
      name: "Cases",
      component: (
        <CasesStackList
          module_param={item.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Ordrer",
      component: (
        <OrdersStacklist
          module_param={item.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Reports",
      component: (
        <ReportsStackList
          module_param={item.id}
          module="customer"
          showDeleted={true}
          fields="id, displayTitle, report_number, status, executing_employee, delivery_address, customer, report_type, appointment, description"
          filters={[
            {
              name: "customer",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Agreements",
      component: (
        <ServiceAgreementsStackList
          module_param={item.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Projects",
      href: "#",
      component: (
        <ProjectsStackList
          module_param={item.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: item.id,
            },
          ]}
        />
      ),
    },
  ];

  const [deletePromptOpen, setDeletePromptOpen] = useState(false);

  const navigate = useNavigate();
  const canEditCustomer = useUserHasAccess({ role: "customers", action: "edit" });
  const canCreateCustomer = useUserHasAccess({ role: "customers", action: "create" });
  const canDeleteCustomer = useUserHasAccess({ role: "customers", action: "delete" });

  const removeCustomer = async (customer) => {
    axiosWithToast(
      destroyCustomer(customer),
      () => {},
      () => navigate("/dashboard/customers")
    );
  };

  const headerActions = [
    ...(canDeleteCustomer ? [{
      component: null,
      label: "Slett",
      description: "",
      path: null,
      action: () => setDeletePromptOpen(true),
      icon: TrashIcon,
      textColor: "text-red-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    }] : []),
    {
      component: (
        <HistoriesModalButton module="customer" module_param={customer?.id} />
      ),
      path: null,
      hidden: false,
      menu: false,
    },
    ...(canEditCustomer
      ? [
          {
            component: null,
            label: "Edit",
            description: "",
            path: `/dashboard/customers/${customer?.id}/edit`,
            icon: PencilIcon,
            textColor: "text-blue-700",
            bgColor: "bg-white",
            hidden: false,
            menu: false,
          },
        ]
      : []),
  ];

  function customerTabs() { return null; }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="">
          <ResourceLoading />
        </div>
      );
    }
    if (error && errors) {
      return <ResourceError errors={errors} />;
    }

    if (!item?.id) {
      return <ResourceEmpty />;
    }

    return (
      <>
        <DashboardHeader
          subtitle=""
          actions={headerActions}
          title={customer?.customer_number + " " + customer?.name}
          meta={[]}
          {...(canCreateCustomer ? {
            mainButton: {
            label: "New",
            action: () => setCaseDialogOpen(true),
              path: null,
              color: "",
              icon: PlusIcon,
            },
          } : {mainButton: null})}
        >
          <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="order-2 lg:order-1 lg:col-span-2">
                  <PMTabs tabs={tabs} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
                  <div className="py-5">{tabs[selectedTab].component}</div>
                </div>
                <div className="order-1 lg:order-2 lg:col-span-1">
                  <PMLinkCard actions={rightSideActions} />
                </div>
              </div>

              <ServiceReportEditModal
                customer={item}
                open={reportDialogOpen}
                onClose={() => setReportDialogOpen(false)}
                selected={null}
                onRefresh={() => null}
              />

              <CaseDialog
                customer={item}
                module_param={item.id}
                module="customer"
                open={caseDialogOpen}
                onClose={() => setCaseDialogOpen(false)}
                onRefresh={() => props.onRefresh()}
              />

              <ServiceAgreementModal
                open={serviceAgreementDialogOpen}
                onClose={() => setServiceAgreementDialogOpen(false)}
                customer={item}
              />

              <ProjectDialog
                open={projectDialogOpen}
                onClose={() => setProjectDialogOpen(false)}
                readOnly={false}
                customer={item}
              />
            </div>
          </div>
        </DashboardHeader>

        <SimplePrompt
          title="Sikker på at du vill lukke saken?"
          description="Kunden slettes permanent"
          onAccept={() => removeCustomer(item)}
          open={deletePromptOpen}
          onClose={() => setDeletePromptOpen(false)}
        />
      </>
    );
  };

  return <>{renderContent()}</>;
};
